/**
 * Protected Route - Vereist authenticatie en optioneel permissies
 */

import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { useAuth } from '../context/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermission?: string | string[];
  requireAll?: boolean; // If true, all permissions are required; if false, any one is enough
}

const ProtectedRoute = ({
  children,
  requiredPermission,
  requireAll = false
}: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const isAuthenticated = authService.isAuthenticated();
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Wait for auth context to load
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.1rem',
        color: '#666'
      }}>
        Laden...
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Check permissions if required
  if (requiredPermission) {
    let hasRequiredPermission = false;

    if (Array.isArray(requiredPermission)) {
      hasRequiredPermission = requireAll
        ? hasAllPermissions(requiredPermission)
        : hasAnyPermission(requiredPermission);
    } else {
      hasRequiredPermission = hasPermission(requiredPermission);
    }

    if (!hasRequiredPermission) {
      return (
        <div className="page-container">
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            maxWidth: '500px',
            margin: '2rem auto'
          }}>
            <h2 style={{ color: '#dc3545', marginBottom: '1rem' }}>
              Geen Toegang
            </h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              Je hebt niet de juiste rechten om deze pagina te bekijken.
            </p>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#4A9EFF',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Terug naar Dashboard
            </button>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
